import { Dialog } from "@ark-ui/react";
import { Chip } from "@base/ui/Chip";
import { SidePanel } from "@base/ui/SidePanel";
import { adminQueries } from "@features/admin/api/queryFactory";
import { formatPeriod, getStatusTranslationKey, isActiveStatus } from "@features/admin/lib";
import type { MemberListItem, MemberSubscription } from "@features/admin/model/types";
import { useQuery } from "@tanstack/react-query";
import { Mail, Phone, User, X } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

interface PlanCardProps {
  subscription: MemberSubscription;
}

const PlanCard = ({ subscription }: PlanCardProps) => {
  const { t } = useTranslation();
  const active = isActiveStatus(subscription.status);

  return (
    <div className="p-4 space-y-2 border border-gray-200 rounded-8 bg-gray-10">
      <h4 className="text-subtitle4 text-gray-1000">{t(`memberType_${subscription.type}`)}</h4>
      <div className="flex justify-between items-center">
        <span className="text-gray-700 text-label2">{t("adminColumnStatus")}</span>
        <Chip size="sm" variant="fill" color={active ? "info" : "error"}>
          {t(getStatusTranslationKey(subscription.status))}
        </Chip>
      </div>
      <div className="flex justify-between items-center">
        <span className="text-gray-700 text-label2">{t("adminColumnPeriod")}</span>
        <span className="text-gray-1000 text-label2">
          {formatPeriod(
            { start_date: subscription.start_date, end_date: subscription.end_date },
            subscription.status,
            subscription.type,
          )}
        </span>
      </div>
    </div>
  );
};

interface ContactRowProps {
  icon: React.ReactNode;
  label: string;
  value: string;
}

const ContactRow = ({ icon, label, value }: ContactRowProps) => (
  <div className="flex gap-3 items-center">
    <div className="flex justify-center items-center w-10 h-10 bg-gray-100 shrink-0 rounded-8 text-gray-700">
      {icon}
    </div>
    <div className="min-w-0">
      <p className="text-gray-500 text-label3">{label}</p>
      <p className="truncate text-gray-1000 text-body4">{value}</p>
    </div>
  </div>
);

export interface MemberDetailPanelProps {
  member: MemberListItem | null;
  onClose: () => void;
}

export const MemberDetailPanel = ({ member, onClose }: MemberDetailPanelProps) => {
  const { t } = useTranslation();
  const isOpen = !!member;

  const { data: profile } = useQuery({
    ...adminQueries.memberProfile(member?.user_id ?? 0),
    enabled: isOpen,
  });
  const { data: subscriptions } = useQuery({
    ...adminQueries.memberSubscriptions(member?.company_id ?? 0),
    enabled: isOpen,
  });

  const { currentPlan, previousPlans } = useMemo(() => {
    const items = subscriptions?.subscriptions ?? [];
    const actives = items
      .filter((item) => isActiveStatus(item.status))
      .sort((a, b) => b.start_date.localeCompare(a.start_date));
    const current = actives[0] ?? null;
    const previous = items.filter((item) => item !== current).sort((a, b) => b.start_date.localeCompare(a.start_date));

    return { currentPlan: current, previousPlans: previous };
  }, [subscriptions]);

  return (
    <SidePanel open={isOpen} onClose={onClose}>
      <div className="flex justify-between items-center px-6 py-5 border-b border-gray-200">
        <Dialog.Title asChild>
          <h3 className="text-subtitle3 text-gray-1000">{t("adminMemberDetails")}</h3>
        </Dialog.Title>
        <button type="button" onClick={onClose} aria-label={t("adminClose")} className="text-gray-900">
          <X size={20} />
        </button>
      </div>

      <div className="overflow-y-auto flex-1 px-6 py-6 space-y-6">
        <h2 className="text-title4 text-gray-1000">{member?.company_name}</h2>

        {currentPlan && <PlanCard subscription={currentPlan} />}

        {profile && (
          <section className="space-y-3">
            <h3 className="text-subtitle4 text-gray-1000">{t("adminContactInformation")}</h3>
            <div className="p-4 space-y-4 border border-gray-200 rounded-8">
              <ContactRow icon={<User size={18} />} label={t("adminRepresentative")} value={profile.name} />
              <ContactRow icon={<Mail size={18} />} label={t("adminEmail")} value={profile.email} />
              <ContactRow icon={<Phone size={18} />} label={t("adminPhoneNumber")} value={profile.phone_number} />
            </div>
          </section>
        )}

        {previousPlans.length > 0 && (
          <section className="space-y-3">
            <h3 className="text-subtitle4 text-gray-1000">{t("adminPreviousPlanHistory")}</h3>
            <div className="space-y-2">
              {previousPlans.map((subscription) => (
                <PlanCard key={subscription.billing_order_id} subscription={subscription} />
              ))}
            </div>
          </section>
        )}
      </div>
    </SidePanel>
  );
};
